/**
 * RightRailArtifacts — the "ARTIFACTS" tab inside the chat workbench's
 * right rail. Lists the files that have been auto-mirrored into
 * /documents/artifacts/ for the current teacher and exposes a
 * Submit-for-approval action on each row.
 *
 * Source of truth: /documents/artifacts/ on Nova OS document storage.
 * The chat surface's ArtifactAutoSave watcher writes every assistant
 * PPTX / DOCX / PDF here, so the rail mirrors the same list that
 * NotesFilesPage's 生成产物 panel shows. There's no separate "chat
 * artifacts" model — the file IS the artifact.
 *
 * Submit-for-approval reuses PromoteFileButton from FolderPanel, so
 * the modal + /api/school/promotions/submit flow are identical to
 * the /documents page. Single source of truth, single working flow.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import {
  deleteDoc,
  formatFileTime,
  listDocs,
  sortFilesByTimeDesc,
} from "../teachingPlans/documentsApi";
import { FilePreviewModal } from "./FilePreviewModal";
import { authHeaders } from "../../api/school";
import { downloadWithAuth } from "../chat/attachments/authDownload";
import { PromoteFileButton } from "./FolderPanel";

export function RightRailArtifacts(): JSX.Element {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [preview, setPreview] = useState<string | null>(null);
  // Cache the RAW DocTree under the shared ["documents-tree", subdir]
  // key — FolderPanel caches the same shape there, so either consumer
  // can serve/refresh the other. The rail's sorted view is derived at
  // render time. (Previously this cached a mapped array under the same
  // key, which poisoned the 生成产物 panel's cache after navigation —
  // same bug class as the 我的资料 browser collision, 2026-07-28.)
  const tree = useQuery({
    queryKey: ["documents-tree", "artifacts"],
    queryFn: () => listDocs("artifacts"),
    refetchInterval: 15_000,
  });
  const rows = sortFilesByTimeDesc(tree.data?.files ?? []).map((f) => ({
    name: f.name,
    createdAt: f.created_at ?? "",
  }));

  // Same endpoint + confirm flow as FolderPanel on /documents — the rail
  // and the 生成产物 panel list the same files, so deleting here must
  // refresh both (shared "documents-tree" query keys).
  const remove = useMutation({
    mutationFn: (name: string) => deleteDoc(`artifacts/${name}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["documents-tree", "artifacts"] });
      void qc.invalidateQueries({ queryKey: ["documents-tree"] });
      void qc.invalidateQueries({ queryKey: ["documents-browser"] });
    },
  });

  function askDelete(name: string) {
    const ok = window.confirm(t("documents.files.deleteConfirmFile", { name }));
    if (!ok) return;
    remove.mutate(name);
  }

  if (tree.isLoading) {
    return (
      <div className="teacher-rail-content">
        <p className="teacher-empty">{String(t("common.loading"))}</p>
      </div>
    );
  }
  if (tree.error) {
    return (
      <div className="teacher-rail-content">
        <p className="teacher-empty" style={{ color: "#b91c1c" }}>
          {(tree.error as Error).message}
        </p>
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div className="teacher-rail-content">
        <p className="teacher-empty">
          {String(t("workbench.tabs.artifactsEmpty"))}
        </p>
      </div>
    );
  }

  return (
    <div className="teacher-rail-content teacher-stack">
      {rows.map((f) => (
        <article key={f.name} className="teacher-card group">
          <div className="teacher-card__topline">
            <span className="teacher-card__title truncate">{f.name}</span>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <button
              type="button"
              onClick={() => setPreview(f.name)}
              className="text-xs px-2 py-0.5 rounded text-slate-700 hover:bg-slate-100"
            >
              {t("common.view")}
            </button>
            <button
              type="button"
              title={String(t("documents.files.downloadHint"))}
              onClick={() => {
                void downloadWithAuth(
                  `/api/documents/download/${encodeURIComponent(`artifacts/${f.name}`)}`,
                  authHeaders(),
                ).catch(() => {});
              }}
              className="text-xs px-2 py-0.5 rounded text-slate-700 hover:bg-slate-100"
            >
              {t("documents.files.download")}
            </button>
            {f.createdAt && (
              <span className="text-[10px] text-slate-400 whitespace-nowrap">
                {formatFileTime(f.createdAt)}
              </span>
            )}
            {/* PromoteFileButton renders the same "Submit for approval"
                pill + modal flow as /documents. Note the parent .group
                class above so its hover-reveal is triggered by hovering
                anywhere on the card, not just the button area. */}
            <PromoteFileButton subdir="artifacts" filename={f.name} />
            <button
              type="button"
              onClick={() => askDelete(f.name)}
              disabled={remove.isPending}
              className="opacity-0 group-hover:opacity-100 text-xs px-2 py-0.5 rounded text-rose-600 hover:bg-rose-50 disabled:opacity-30 transition-opacity"
            >
              {t("documents.files.delete")}
            </button>
          </div>
        </article>
      ))}
      {preview && (
        <FilePreviewModal
          path={`artifacts/${preview}`}
          filename={preview}
          onClose={() => setPreview(null)}
        />
      )}
    </div>
  );
}
